import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import BrepLogo from "./BrepLogo";
import AuthModal from "./AuthModal";
import { useAuth } from "../lib/auth";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/coaches", label: "For Coaches" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/download", label: "Download App" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `text-[12px] tracking-[0.22em] uppercase transition-colors ${
      isActive ? "text-white" : "text-white/60 hover:text-white"
    }`;

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#050510]/70 border-b border-white/5"
        data-testid="site-nav"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo" aria-label="BRep home">
            <BrepLogo size="sm" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="hidden md:flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase">
                <span className="text-white/70" data-testid="nav-user-name">Hi, {user.first_name}</span>
                <button
                  type="button"
                  onClick={logout}
                  className="inline-flex items-center gap-1.5 text-white/50 hover:text-[#2A5BFF] transition-colors"
                  data-testid="nav-logout"
                  aria-label="Sign out"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAuthOpen(true)}
                className="hidden md:inline-flex text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white transition-colors"
                data-testid="nav-signin"
              >
                Sign in
              </button>
            )}
            <Link
              to="/download"
              className="hidden md:inline-flex btn-primary px-4 py-2 text-[11px] tracking-[0.24em] uppercase font-medium"
              data-testid="nav-cta-download"
            >
              Download App
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/15 hover:border-[#2A5BFF] transition-colors"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="nav-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-[#050510]/98 backdrop-blur-md lg:hidden pt-20"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-6 py-6 font-anton uppercase text-3xl ${
                        isActive ? "text-[#2A5BFF]" : "text-white"
                      }`
                    }
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span>{l.label}</span>
                    <span className="text-[10px] tracking-[0.28em] text-white/40 font-manrope">{String(i + 1).padStart(2, "0")}</span>
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ x: 30, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.05 + LINKS.length * 0.04, duration: 0.35 }}
                className="p-6 space-y-4"
              >
                {user ? (
                  <button
                    type="button"
                    onClick={() => { logout(); setOpen(false); }}
                    className="w-full block text-center px-6 py-4 text-[12px] tracking-[0.24em] uppercase text-white/70 hover:text-[#2A5BFF] border border-white/15 hover:border-[#2A5BFF] transition-colors"
                    data-testid="mobile-nav-signout"
                  >
                    Sign out
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setAuthOpen(true); }}
                    className="w-full block text-center px-6 py-4 text-[12px] tracking-[0.24em] uppercase text-white/70 hover:text-white border border-white/15 hover:border-white transition-colors"
                    data-testid="mobile-nav-signin"
                  >
                    Sign in
                  </button>
                )}
                <Link
                  to="/download"
                  className="btn-primary block text-center px-6 py-4 text-[12px] tracking-[0.24em] uppercase font-medium"
                  data-testid="mobile-nav-download-cta"
                >
                  Download BRep
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  );
}
